import React, { useState } from 'react';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { sendFeedback } from '../../../store/actions/FeedbackAction';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Button from '../../shared/TailwindComponents/Button';
import clsx from 'clsx';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, feedbackTourSteps } from '../../shared/FeatureTour';

const MAX_WORD_LIMIT = 500;

const Feedback: React.FC = () => {
  const [feedback, setFeedback] = useState('');
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, AnyAction>>();
  const { t } = useTranslation();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'feedback',
    steps: feedbackTourSteps,
    autoStartDelay: 600,
  });

  const handleSubmitFeedback = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isOverLimit) {
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Your feedback exceeds the 500-word limit.'),
          duration: 3000,
          showModal: true,
        })
      );
      return;
    }
    if (!feedback.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        sendFeedback({
          FeedbackContent: feedback,
        })
      );
      setFeedback('');
    } catch (error) {
      console.error('Failed to send feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
    setFeedback(text);
    setIsOverLimit(wordCount > MAX_WORD_LIMIT);
  };

  const wordCount = feedback.trim() ? feedback.trim().split(/\s+/).length : 0;

  return (
    <>
    <div data-tour-id="feedback-form" className="flex flex-col items-left justify-start w-full h-full max-w-4xl mx-auto px-4 mt-12">
      <h2 className="text-xl font-semibold mb-4">{t('Share Your Feedback')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-8">
        {t('We appreciate your insights on usability and design, features and functionality, performance and reliability, security and privacy, legal and compliance aspects, support resources, or any other area to help us improve the platform.')}
      </p>
      <form className="w-full" onSubmit={handleSubmitFeedback}>
        <Textarea
          id="feedback-input"
          placeholder={t('Enter your feedback here...')}
          value={feedback}
          onChange={handleFeedbackChange}
          className="w-full h-64 bg-white dark:bg-black"
          label={''}
        />
        <div className="flex justify-between items-center mt-2">
          <span className={clsx('text-sm', isOverLimit ? 'text-red-500' : 'text-gray-500')}>
            {wordCount} / {MAX_WORD_LIMIT}
          </span>
          {isOverLimit && (
            <p className="text-sm text-red-500">{t('You have exceeded the 500-word limit')}</p>
          )}
          <Button
            type="submit"
            size="small"
            variant="primary"
            disabled={!feedback.trim() || isOverLimit || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('Send')}
          </Button>
        </div>
      </form>
    </div>
    <FeatureTour
      steps={feedbackTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

export default Feedback;
